// fabric not needed directly here

export class CustomControl {
  constructor(canvasObject, options = {}) {
    this.canvas = window.canvas;
    this.canvasObject = canvasObject;
    this.element = null;
    this.isVisible = false;
    this.clickListeners = [];
    this.options = {
      offsetX: 0,
      offsetY: 0,
      zIndex: 1000,
      className: 'custom-control',
      // Global scale to make controls uniformly smaller/larger on screen
      scale: 0.8,
      gap: 12, // default gap between object and controls
      ...options,
    };

    this.boundUpdatePosition = this.updatePosition.bind(this);
    this.boundHandleCanvasZoom = this.handleCanvasZoom.bind(this);
    this.boundHandleObjectRemoved = this.handleObjectRemoved.bind(this);

    // Internal state for stable placement and throttling
    this._placement = null; // 'top' | 'bottom' | 'left' | 'right'
    this._rafScheduled = false;
    this._rafId = null;
    this._tempHidden = false;
    this._wasVisibleBeforeInteraction = false;
    this._layoutTransitioning = false;

    this.boundHideOnMoveCanvas = this.hideOnCanvasInteraction.bind(this);
    this.boundShowOnEndCanvas = this.showOnCanvasInteractionEnd.bind(this);

    this.init();
  }

  init() {
    this.beforeCreateElement();
    this.createElement();
    this.attachToCanvas();
    this.setupEventListeners();
  }

  beforeCreateElement() {
    // Hook for subclasses to initialize properties before createElement is called
  }

  createElement() {
    throw new Error('createElement must be implemented by subclass');
  }

  attachToCanvas() {
    // Attach to body and use position: fixed so layout transitions don't affect controls
    const container = document.body;
    container.appendChild(this.element);
    this.element.style.position = 'fixed';
    this.element.style.zIndex = this.options.zIndex;

    // Ensure SVG icons inside this control are injected after being added to DOM
    try {
      const { injectSvgIcons } = require('~src/ui');
      if (typeof injectSvgIcons === 'function') {
        injectSvgIcons();
      }
    } catch (e) {
      console.error('Failed injecting SVG icons into control element', e);
    }
  }

  setupEventListeners() {
    this.canvas.on('object:moving', this.boundUpdatePosition);
    this.canvas.on('object:scaling', this.boundUpdatePosition);
    this.canvas.on('object:rotating', this.boundUpdatePosition);
    this.canvas.on('object:modified', this.boundUpdatePosition);
    this.canvas.on('after:render', this.boundUpdatePosition);
    this.canvas.on('zoom:changed', this.boundHandleCanvasZoom);

    this.canvas.on('object:removed', this.boundHandleObjectRemoved);

    this.canvasObject.on('moving', () => {
      this.hideTemporarily();
      this.boundUpdatePosition();
    });
    this.canvasObject.on('scaling', () => {
      this.hideTemporarily();
      this.boundUpdatePosition();
    });
    this.canvasObject.on('rotating', () => {
      this.hideTemporarily();
      this.boundUpdatePosition();
    });
    this.canvasObject.on('modified', () => {
      this.showIfTemporarilyHidden();
      this.boundUpdatePosition();
    });

    // Also respond to canvas-level interactions (e.g., when editing image inside MagicFrame moves)
    this.canvas.on('object:moving', this.boundHideOnMoveCanvas);
    this.canvas.on('object:scaling', this.boundHideOnMoveCanvas);
    this.canvas.on('object:rotating', this.boundHideOnMoveCanvas);
    this.canvas.on('object:modified', this.boundShowOnEndCanvas);
    this.canvas.on('mouse:up', this.boundShowOnEndCanvas);

    window.addEventListener('resize', this.boundUpdatePosition);

    // Observe CSS zoom/margin changes on #canvas-zoom to keep alignment
    this.zoomEl = document.getElementById('canvas-zoom');
    if (this.zoomEl && typeof MutationObserver !== 'undefined') {
      this.zoomObserver = new MutationObserver(() => this.updatePosition());
      this.zoomObserver.observe(this.zoomEl, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    // Watch for sidebar/canvas layout transitions to avoid jitter when opening sidebar
    this.setupLayoutTransitionListeners();
  }

  // Parse CSS transition durations (can be comma-separated). Returns ms.
  _parseTransitionMs(durations, delays) {
    const toMs = (str) => {
      if (!str) return 0;
      return str
        .split(',')
        .map((s) => s.trim())
        .map((s) => (s.endsWith('ms') ? parseFloat(s) : parseFloat(s) * 1000))
        .filter((n) => !isNaN(n))
        .reduce((a, b) => Math.max(a, b), 0);
    };
    return toMs(durations) + toMs(delays);
  }

  setupLayoutTransitionListeners() {
    const sidebarEl = document.getElementById('editor-sidebar');
    const canvasContainer = document.getElementById('canvas-container');
    const sidebarContent = document.getElementById('sidebar-content');
    const layoutContainer = document.querySelector('.layout-editor-container');
    const targets = [
      sidebarEl,
      sidebarContent,
      canvasContainer,
      layoutContainer,
    ].filter(Boolean);

    const onStart = () => {
      if (!this._layoutTransitioning) {
        this._layoutTransitioning = true;
        // Keep current placement during layout shift to avoid side flipping
        this._suspendRepickOnce = true;
        this.hideTemporarily();
      }
    };
    const onEnd = () => {
      if (this._layoutTransitioning) {
        this._layoutTransitioning = false;
        this.showIfTemporarilyHidden();
        this.updatePosition();
      }
    };

    this._layoutTargets = targets;
    this._onLayoutStart = onStart;
    this._onLayoutEnd = onEnd;

    // Listen to CSS transitions (preferred). Ignore transitions from descendants
    // and only react to structural properties that can affect placement.
    const structuralProps = new Set([
      'left',
      'right',
      'top',
      'bottom',
      'width',
      'height',
      'margin-left',
      'margin-right',
      'margin-top',
      'margin-bottom',
      'transform',
    ]);
    targets.forEach((el) => {
      const handleStart = (ev) => {
        try {
          if (ev.target !== el) return; // ignore descendant transitions
          const pn = ev.propertyName || '';
          if (pn && !structuralProps.has(pn)) return; // ignore non-structural
        } catch (e) {
          console.error('Failed hiding control during canvas interaction', e);
        }
        onStart();
      };
      const handleEnd = (ev) => {
        try {
          if (ev.target !== el) return;
          const pn = ev.propertyName || '';
          if (pn && !structuralProps.has(pn)) return;
        } catch (e) {
          console.error(
            'Failed finalizing control after canvas interaction',
            e
          );
        }
        onEnd();
      };
      el.addEventListener('transitionstart', handleStart);
      el.addEventListener('transitionend', handleEnd);
      // Save handlers for later removal if needed
      if (!this._layoutHandlers) this._layoutHandlers = new Map();
      this._layoutHandlers.set(el, { handleStart, handleEnd });
    });

    // Fallback: observe class/style mutations and simulate transition window
    if (typeof MutationObserver !== 'undefined') {
      this._layoutMutationObservers = targets.map((el) => {
        const obs = new MutationObserver(() => {
          // Estimate transition time
          const cs = window.getComputedStyle(el);
          const ms = this._parseTransitionMs(
            cs.transitionDuration,
            cs.transitionDelay
          );
          if (ms > 0) {
            onStart();
            if (this._layoutEndTimer) clearTimeout(this._layoutEndTimer);
            this._layoutEndTimer = setTimeout(onEnd, ms + 50);
          } else {
            // No transition, just update position
            this.updatePosition();
          }
        });
        obs.observe(el, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        return obs;
      });
    }
  }

  hideOnCanvasInteraction(e) {
    const t = e && e.target;
    if (!t) return;
    // Hide if the moving/scaling/rotating target is this object or its child image
    if (t === this.canvasObject || t._magicFrame === this.canvasObject) {
      this.hideTemporarily();
    }
  }

  showOnCanvasInteractionEnd(e) {
    const t = e && e.target;
    if (!t) return;
    if (t === this.canvasObject || t._magicFrame === this.canvasObject) {
      this.showIfTemporarilyHidden();
      this.updatePosition();
    }
  }

  hideTemporarily() {
    if (!this.element) return;
    if (!this.isVisible) return; // respect logical visibility
    if (!this._tempHidden) {
      this._wasVisibleBeforeInteraction = true;
      this.element.style.display = 'none';
      this._tempHidden = true;
    }
  }

  showIfTemporarilyHidden() {
    if (!this.element) return;
    if (this._tempHidden && this._wasVisibleBeforeInteraction) {
      this.element.style.display = 'block';
      // Force placement recompute after ending interaction unless suspended
      if (!this._suspendRepickOnce) {
        this._placement = null;
      }
      this._tempHidden = false;
      this._wasVisibleBeforeInteraction = false;
      this._suspendRepickOnce = false;
    }
  }

  calculateCanvasIndependentScale() {
    // Keep UI at a constant on-screen size, independent of canvas size/zoom
    // Allow a global scale factor via options
    return this.options.scale || 1;
  }

  updatePosition() {
    if (!this.canvasObject || !this.element || !this.isVisible) return;

    // Throttle to next animation frame to avoid mid-layout jitter
    if (this._rafScheduled) return;
    this._rafScheduled = true;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._rafScheduled = false;

      // Re-check state after async boundary; the control may have been destroyed
      if (
        !this.canvas ||
        !this.canvasObject ||
        !this.element ||
        !this.isVisible
      )
        return;

      if (typeof this.canvas.getElement !== 'function') return;
      const canvasElement = this.canvas.getElement();
      if (!canvasElement) return;
      const canvasRect = canvasElement.getBoundingClientRect();

      // Ensure coords are up to date
      if (
        this.canvasObject &&
        typeof this.canvasObject.setCoords === 'function'
      ) {
        this.canvasObject.setCoords();
      }

      // Use absolute coordinates in canvas space
      const a = this.canvasObject && this.canvasObject.aCoords;
      if (!a) return;

      // Object bounds (canvas coordinates)
      const objLeftC = Math.min(a.tl.x, a.bl.x);
      const objRightC = Math.max(a.tr.x, a.br.x);
      const objTopC = Math.min(a.tl.y, a.tr.y);
      const objBottomC = Math.max(a.bl.y, a.br.y);
      const objCenterXC = (objLeftC + objRightC) / 2;
      const objCenterYC = (objTopC + objBottomC) / 2;

      // Convert canvas coordinates to viewport pixels considering CSS zoom
      const scaleX = canvasRect.width / this.canvas.getWidth();
      const scaleY = canvasRect.height / this.canvas.getHeight();

      // Convert to viewport pixels (relative to body)
      const objLeft = canvasRect.left + objLeftC * scaleX;
      const objRight = canvasRect.left + objRightC * scaleX;
      const objTop = canvasRect.top + objTopC * scaleY;
      const objBottom = canvasRect.top + objBottomC * scaleY;
      const objCenterX = canvasRect.left + objCenterXC * scaleX;
      const objCenterY = canvasRect.top + objCenterYC * scaleY;

      // Bounds: canvas and viewport. Prefer to keep inside both when possible.
      const cLeft = canvasRect.left;
      const cTop = canvasRect.top;
      const cRight = canvasRect.right;
      const cBottom = canvasRect.bottom;
      const vLeft = 0;
      // Respect fixed navbars occupying the top of the viewport (e.g., #navbar-canvas height ~50px)
      let vTop = 0;
      try {
        const navs = [
          document.getElementById('navbar-canvas'),
          document.getElementById('navbar-private'),
        ].filter(Boolean);
        for (const nav of navs) {
          const r = nav.getBoundingClientRect();
          const visible =
            r.height > 0 &&
            r.width > 0 &&
            getComputedStyle(nav).display !== 'none';
          if (visible) {
            vTop = Math.max(vTop, r.bottom);
          }
        }
      } catch (e) {
        console.error('Failed reading navbars when placing control', e);
      }
      const vRight = window.innerWidth || document.documentElement.clientWidth;
      const vBottom =
        window.innerHeight || document.documentElement.clientHeight;

      // Available space around object inside canvas and viewport
      const spaceTopCanvas = Math.max(0, objTop - cTop);
      const spaceBottomCanvas = Math.max(0, cBottom - objBottom);
      const spaceLeftCanvas = Math.max(0, objLeft - cLeft);
      const spaceRightCanvas = Math.max(0, cRight - objRight);

      const spaceTopViewport = Math.max(0, objTop - vTop);
      const spaceBottomViewport = Math.max(0, vBottom - objBottom);
      const spaceLeftViewport = Math.max(0, objLeft - vLeft);
      const spaceRightViewport = Math.max(0, vRight - objRight);

      // Measure control size (includes transform scale)
      const controlRect = this.element.getBoundingClientRect();
      let controlWidth = controlRect.width;
      let controlHeight = controlRect.height;
      if (controlWidth <= 0 || controlHeight <= 0) {
        controlWidth = this._lastControlWidth || 220; // fallback to ~min width
        controlHeight = this._lastControlHeight || 80;
      } else {
        this._lastControlWidth = controlWidth;
        this._lastControlHeight = controlHeight;
      }

      const gap = this.options.gap || 0;

      const needH = controlHeight + gap;
      const needW = controlWidth + gap;

      // compute view/canvas spaces (derived below in scoreSide)

      // Removed unused helpers fitsBoth/fitsViewport/chooseByViewportSpace

      // Scoring-based placement: prefer viewport fit, boost if also fits canvas
      // Also consider nested submenus space so menus remain visible when opened.
      const submenuDepth = 2; // current nested levels: submenu + nested submenu
      const submenuWidthEstimate = 240; // px, approx width of submenu
      const submenuGutter = 16; // px tolerance
      const submenuBudget = submenuDepth * submenuWidthEstimate + submenuGutter;

      const scoreSide = (side) => {
        switch (side) {
          case 'top': {
            if (spaceTopViewport < needH) return -1;
            let score = spaceTopViewport;
            if (spaceTopCanvas >= needH) score += needH;
            // For top/bottom, ensure enough horizontal room on either side for submenus
            const maxHorz = Math.max(spaceLeftViewport, spaceRightViewport);
            if (maxHorz < submenuBudget) score -= submenuBudget; // penalize
            return score;
          }
          case 'bottom': {
            if (spaceBottomViewport < needH) return -1;
            let score = spaceBottomViewport;
            if (spaceBottomCanvas >= needH) score += needH;
            const maxHorz = Math.max(spaceLeftViewport, spaceRightViewport);
            if (maxHorz < submenuBudget) score -= submenuBudget;
            return score;
          }
          case 'right': {
            if (spaceRightViewport < needW) return -1;
            let score = spaceRightViewport;
            if (spaceRightCanvas >= needW) score += needW;
            // Prefer right only if we can afford submenus on the right,
            // otherwise penalize and favor left/top/bottom
            if (
              spaceRightViewport < submenuBudget &&
              spaceLeftViewport > spaceRightViewport
            ) {
              score -= submenuBudget;
            }
            return score;
          }
          case 'left':
          default: {
            if (spaceLeftViewport < needW) return -1;
            let score = spaceLeftViewport;
            if (spaceLeftCanvas >= needW) score += needW;
            if (
              spaceLeftViewport < submenuBudget &&
              spaceRightViewport > spaceLeftViewport
            ) {
              score -= submenuBudget;
            }
            return score;
          }
        }
      };

      const allowRepick = !this._tempHidden;
      if (allowRepick) {
        // px threshold for viewport edge proximity (informative)

        const objType = this.canvasObject && this.canvasObject.type;
        const isText = objType === 'textbox' || objType === 'i-text';
        const preferVertical = !!this.options.preferVerticalPlacement;
        const isTextLike = isText || preferVertical;

        // Strategy for text: prefer ABOVE (top). X only clamps horizontally; do not switch to left/right due to side proximity.
        if (isTextLike) {
          if (spaceTopViewport >= needH) {
            this._placement = 'top';
          } else if (spaceBottomViewport >= needH) {
            this._placement = 'bottom';
          } else {
            // Fallback: choose the vertical side with more viewport room
            this._placement =
              spaceTopViewport >= spaceBottomViewport ? 'top' : 'bottom';
          }
        } else {
          // Strategy for images/magic frames: prefer RIGHT whenever the page allows,
          // accounting for nested submenus that expand width to the right.
          const rightHasBudget = spaceRightViewport >= needW + submenuBudget;
          const leftHasBudget = spaceLeftViewport >= needW + submenuBudget;

          if (rightHasBudget) {
            this._placement = 'right';
          } else if (leftHasBudget) {
            // If right can't safely host nested submenus, try left
            this._placement = 'left';
          } else if (
            spaceRightViewport >= needW ||
            spaceLeftViewport >= needW
          ) {
            // Neither side has full submenu budget, fall back to the side with more horizontal space
            this._placement =
              spaceRightViewport >= spaceLeftViewport ? 'right' : 'left';
          }
        }

        const candidates = [
          { p: 'top', sc: scoreSide('top') },
          { p: 'bottom', sc: scoreSide('bottom') },
          { p: 'right', sc: scoreSide('right') },
          { p: 'left', sc: scoreSide('left') },
        ];
        const currentScore = this._placement ? scoreSide(this._placement) : -1;
        if (currentScore < 0) {
          if (isTextLike) {
            // For text, stick to vertical placement even if cramped
            this._placement =
              spaceTopViewport >= spaceBottomViewport ? 'top' : 'bottom';
          } else {
            // Prefer right by default in tie-breakers; sort by score then bias right
            candidates.sort((a, b) => b.sc - a.sc);
            const best = candidates.find((c) => c.sc >= 0) || candidates[0];
            this._placement = best.p;
          }
        }
      }

      // Anchor coordinates and transform by placement
      let anchorX = objCenterX;
      let anchorY = objTop;
      let transform = '';
      const scale = this.calculateCanvasIndependentScale();

      // Clamp within viewport (page), not canvas, so controls can use page space
      const iLeft = vLeft;
      const iTop = vTop;
      const iRight = vRight;
      const iBottom = vBottom;

      switch (this._placement) {
        case 'top':
          // Anchor is the bottom-center of the panel (translateY(-100%))
          // Ensure entire panel stays within viewport horizontally and vertically
          anchorX = Math.min(
            Math.max(objCenterX, iLeft + controlWidth / 2 + 4),
            iRight - controlWidth / 2 - 4
          );
          anchorY = Math.max(
            objTop - gap,
            // Keep full panel inside viewport: bottom (anchorY) must be at least top + gap + height
            iTop + gap + controlHeight
          );
          transform = `translate(-50%, -100%) scale(${scale})`;
          break;
        case 'bottom':
          // Anchor is the top-center of the panel
          anchorX = Math.min(
            Math.max(objCenterX, iLeft + controlWidth / 2 + 4),
            iRight - controlWidth / 2 - 4
          );
          anchorY = Math.min(
            objBottom + gap,
            // Keep full panel inside viewport: top (anchorY) must be at most bottom - gap - height
            iBottom - gap - controlHeight
          );
          transform = `translate(-50%, 0%) scale(${scale})`;
          break;
        case 'right':
          // Anchor is the left-center of the panel
          anchorX = Math.min(
            objRight + gap,
            // Keep full panel inside viewport horizontally
            iRight - gap - controlWidth
          );
          anchorY = Math.min(
            Math.max(objCenterY, iTop + controlHeight / 2 + 4),
            iBottom - controlHeight / 2 - 4
          );
          transform = `translate(0%, -50%) scale(${scale})`;
          break;
        case 'left':
        default:
          // Anchor is the right-center of the panel (translateX(-100%))
          anchorX = Math.max(
            objLeft - gap,
            // Keep full panel inside viewport horizontally
            iLeft + gap + controlWidth
          );
          anchorY = Math.min(
            Math.max(objCenterY, iTop + controlHeight / 2 + 4),
            iBottom - controlHeight / 2 - 4
          );
          transform = `translate(-100%, -50%) scale(${scale})`;
          break;
      }

      // Apply optional offsets from options
      const finalLeft = anchorX + (this.options.offsetX || 0);
      const finalTop = anchorY + (this.options.offsetY || 0);

      this.element.style.left = `${finalLeft}px`;
      this.element.style.top = `${finalTop}px`;
      this.element.style.transform = transform;
      this.element.style.transformOrigin = 'center center';
    });
  }

  handleCanvasZoom() {
    this.updatePosition();
  }

  handleObjectRemoved(e) {
    if (e.target === this.canvasObject) {
      this.destroy();
    }
  }

  show() {
    if (!this.element) return;
    this.isVisible = true;
    this.element.style.display = 'block';
    this._placement = null; // Recompute placement on new show
    // In case new SVGs were added while hidden, reinject now
    try {
      const { injectSvgIcons } = require('~src/ui');
      if (typeof injectSvgIcons === 'function') injectSvgIcons();
    } catch (e) {
      console.error('Failed reinjecting icons on control show', e);
    }
    this.updatePosition();
  }

  hide() {
    if (!this.element) return;
    this.isVisible = false;
    this.element.style.display = 'none';
  }

  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  addClickListener(callback) {
    this.clickListeners.push(callback);
    return () => {
      const index = this.clickListeners.indexOf(callback);
      if (index > -1) {
        this.clickListeners.splice(index, 1);
      }
    };
  }

  handleClick(event) {
    this.clickListeners.forEach((listener) =>
      listener(event, this.canvasObject)
    );
  }

  destroy() {
    // Cancel any pending animation frame
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
      this._rafScheduled = false;
    }
    this.canvas.off('object:moving', this.boundUpdatePosition);
    this.canvas.off('object:scaling', this.boundUpdatePosition);
    this.canvas.off('object:rotating', this.boundUpdatePosition);
    this.canvas.off('object:modified', this.boundUpdatePosition);
    this.canvas.off('after:render', this.boundUpdatePosition);
    this.canvas.off('zoom:changed', this.boundHandleCanvasZoom);
    this.canvas.off('object:removed', this.boundHandleObjectRemoved);

    this.canvas.off('object:moving', this.boundHideOnMoveCanvas);
    this.canvas.off('object:scaling', this.boundHideOnMoveCanvas);
    this.canvas.off('object:rotating', this.boundHideOnMoveCanvas);
    this.canvas.off('object:modified', this.boundShowOnEndCanvas);
    this.canvas.off('mouse:up', this.boundShowOnEndCanvas);

    if (this.canvasObject) {
      if (this._onObjMoving) this.canvasObject.off('moving', this._onObjMoving);
      if (this._onObjScaling)
        this.canvasObject.off('scaling', this._onObjScaling);
      if (this._onObjRotating)
        this.canvasObject.off('rotating', this._onObjRotating);
      if (this._onObjModified)
        this.canvasObject.off('modified', this._onObjModified);
    }

    window.removeEventListener('resize', this.boundUpdatePosition);

    if (this.zoomObserver) {
      this.zoomObserver.disconnect();
      this.zoomObserver = null;
    }

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }

    // Cleanup layout transition listeners
    if (this._layoutTargets) {
      this._layoutTargets.forEach((el) => {
        if (!el) return;
        // Prefer removing specific handlers if present
        const hs = this._layoutHandlers && this._layoutHandlers.get(el);
        if (hs) {
          el.removeEventListener('transitionstart', hs.handleStart);
          el.removeEventListener('transitionend', hs.handleEnd);
        } else {
          if (this._onLayoutStart)
            el.removeEventListener('transitionstart', this._onLayoutStart);
          if (this._onLayoutEnd)
            el.removeEventListener('transitionend', this._onLayoutEnd);
        }
      });
      this._layoutTargets = null;
      this._layoutHandlers = null;
    }
    if (this._layoutMutationObservers) {
      this._layoutMutationObservers.forEach((obs) => obs.disconnect());
      this._layoutMutationObservers = null;
    }
    if (this._layoutEndTimer) {
      clearTimeout(this._layoutEndTimer);
      this._layoutEndTimer = null;
    }

    this.element = null;
    this.canvasObject = null;
    this.clickListeners = [];
  }

  setOptions(newOptions) {
    this.options = { ...this.options, ...newOptions };
    this.updatePosition();
  }

  getElement() {
    return this.element;
  }

  getCanvasObject() {
    return this.canvasObject;
  }
}
